import { supabase } from '@/integrations/supabase/client';
import { SUPABASE_STORAGE_KEY } from '@/lib/config';

/**
 * Get a valid session, refreshing if necessary.
 * Returns null if no valid session can be obtained.
 */
export async function getValidSession() {
  try {
    // First check if auth storage exists - if not, no point trying
    const hasStoredAuth = localStorage.getItem(SUPABASE_STORAGE_KEY);
    if (!hasStoredAuth) {
      return null;
    }
    
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      // Clear any stale storage that might be causing issues
      clearStaleAuthStorage();
      return null;
    }
    
    // Check if token expires within 5 minutes (300 seconds)
    const expiresAt = session.expires_at ?? 0;
    const fiveMinutesFromNow = Math.floor(Date.now() / 1000) + 300;
    
    if (expiresAt < fiveMinutesFromNow) {
      console.log('Session expiring soon, refreshing...');
      const { data: { session: refreshedSession }, error } = await supabase.auth.refreshSession();
      
      if (error || !refreshedSession) {
        console.warn('Failed to refresh session:', error?.message);
        clearStaleAuthStorage();
        return null;
      }
      
      return refreshedSession;
    }
    
    return session;
  } catch (error) {
    console.error('Error getting valid session:', error);
    clearStaleAuthStorage();
    return null;
  }
}

/**
 * Force refresh the session and return the new session.
 * Returns null if refresh fails.
 */
export async function forceRefreshSession() {
  try {
    const { data: { session }, error } = await supabase.auth.refreshSession();
    
    if (error || !session) {
      console.warn('Force refresh failed:', error?.message);
      clearStaleAuthStorage();
      return null;
    }
    
    return session;
  } catch (error) {
    console.error('Error force refreshing session:', error);
    clearStaleAuthStorage();
    return null;
  }
}

/**
 * Clear stale auth storage that might cause cross-domain issues
 */
export function clearStaleAuthStorage() {
  try {
    localStorage.removeItem(SUPABASE_STORAGE_KEY);
  } catch (e) {
    // localStorage might not be available
    console.warn('Could not clear auth storage:', e);
  }
}

/**
 * Check if an error indicates a session expiry issue
 */
export function isSessionExpiredError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = error?.message || error?.error || String(error);
  const errorCode = error?.code;
  
  return (
    errorCode === 'SESSION_EXPIRED' ||
    errorCode === 'PGRST301' ||
    errorMessage.includes('expired') ||
    errorMessage.includes('invalid token') ||
    errorMessage.includes('Invalid token') ||
    errorMessage.includes('JWT expired') ||
    errorMessage.includes('session has expired')
  );
}

/**
 * Check if an error indicates auth is required
 */
export function isAuthRequiredError(error: any): boolean {
  if (!error) return false;
  
  const errorCode = error?.code;
  
  return (
    errorCode === 'AUTH_REQUIRED' ||
    errorCode === 'INVALID_TOKEN'
  );
}
